/**
 * Generated by orval v7.14.0 🍺
 * Do not edit manually.
 * Adobe Photoshop API
 * This is the comprehesive reference for the Adobe Photoshop API, including all available API endpoints, request formats, parameters, authentication requirements, and example responses to use when integrating with or building on top of our APIs.
 * OpenAPI spec version: 1.0
 */
import type {
  ApplyAutoCropRequest,
  ApplyDepthBlurRequest,
  ConvertToActionsJsonRequest,
  CreateArtboardRequest,
  CreateDocumentRequest,
  CreateMaskRequest,
  CreateRenditionRequest,
  DocumentManifestRequest,
  EditTextLayerRequest,
  FacadeJobStatus200,
  FacadeJobStatusPathParameters,
  FillMaskedAreasRequest,
  GetJobStatus200,
  GetJobStatusPathParameters,
  JobLinkResponse,
  JobStatusLinkResponse,
  MaskBodyPartsRequest,
  MaskObjectsRequest,
  ModifyDocumentRequest,
  PlayPhotoshopActionsJsonRequest,
  PlayPhotoshopActionsRequest,
  PsJobResponse,
  PsJobStatusPathParameters,
  RefineMaskRequest,
  RemoveBackgroundRequest,
  RemoveBackgroundV2Request,
  ReplaceSmartObjectRequest,
  SenseiJobApiResponse,
  SenseiJobStatusPathParameters,
} from './photoshop-client.schemas';

import { customPhotoshopAxiosInstance } from '../../mutator/custom-photoshop-axios-instance';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * This endpoint returns the status of a job for a Remove Background and Create Mask operation. The schema of a 200 response varies depending on the status of the job.
 * @summary Get status - mask
 */
export const senseiJobStatus = (
  { jobId }: SenseiJobStatusPathParameters,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<SenseiJobApiResponse>>
) => {
  return customPhotoshopAxiosInstance<SenseiJobApiResponse>(
    { url: `/sensei/status/${jobId}`, method: 'GET' },
    options
  );
};

/**
 * ⚠️ **DEPRECATED**: This endpoint is deprecated and will no longer be accessible after Oct 15, 2025. Use `/v2/remove-background` instead. For more information, see the [Deprecation Announcement](https://developer.adobe.com/firefly-services/docs/photoshop/getting_started/deprecation_announcement/). This endpoint will identify the main subject of your image and removes the background automatically. It also provides options to return a PNG file in 4 channel RGBA or 3 channel RGB format. To check the status of this process, utilize the `Get Status - Mask` endpoint.
 * @deprecated
 * @summary Remove background v1 (Deprecated)
 */
export const removeBackgroundAsync = (
  removeBackgroundRequest: RemoveBackgroundRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/sensei/cutout`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: removeBackgroundRequest,
    },
    options
  );
};

/**
 * This endpoint allows you to remove the background from an image. The request is processed asynchronously and the status of the running job can be checked with the Get Status endpoint.
 * @summary Remove background v2
 */
export const removeBackground = (
  removeBackgroundV2Request: RemoveBackgroundV2Request,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobLinkResponse>(
    {
      url: `/v2/remove-background`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: removeBackgroundV2Request,
    },
    options
  );
};

/**
 * Retrieve the status of a running Remove Background job by providing the job ID. The job ID is obtained from the response of the asynchronous Remove Background API call.
 * @summary Get status - v2
 */
export const facadeJobStatus = (
  { jobId }: FacadeJobStatusPathParameters,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<FacadeJobStatus200>>
) => {
  return customPhotoshopAxiosInstance<FacadeJobStatus200>(
    { url: `/v2/status/${jobId}`, method: 'GET' },
    options
  );
};

/**
 * ⚠️ **DEPRECATED**: This endpoint is deprecated and will no longer be accessible after Oct 15, 2025. Use `/v2/remove-background` instead. For more information, see the [Deprecation Announcement](https://developer.adobe.com/firefly-services/docs/photoshop/getting_started/deprecation_announcement/).This endpoint uses the Remove Background AI/ML model to return a PNG file with a mask applied around the subject. To check the status of this process, utilize the `Get Status - Mask` endpoint.
 * @deprecated
 * @summary Create mask
 */
export const createMaskAsync = (
  createMaskRequest: CreateMaskRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/sensei/mask`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createMaskRequest,
    },
    options
  );
};

/**
 * Get the job status of a Photoshop job.
 * @summary Get status
 */
export const psJobStatus = (
  { jobId }: PsJobStatusPathParameters,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<PsJobResponse>>
) => {
  return customPhotoshopAxiosInstance<PsJobResponse>(
    { url: `/pie/psdService/status/${jobId}`, method: 'GET' },
    options
  );
};

/**
 * Extract metadata from PSD document, including general file and layer information.
 * @summary Extract document manifest
 */
export const getDocumentManifestAsync = (
  documentManifestRequest: DocumentManifestRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/documentManifest`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: documentManifestRequest,
    },
    options
  );
};

/**
 * Create new PSD with layers.
 * @summary Create PSD
 */
export const createDocumentAsync = (
  createDocumentRequest: CreateDocumentRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/documentCreate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createDocumentRequest,
    },
    options
  );
};

/**
 * Apply basic layer edits (name, state, etc.), add/edit adjustment, pixel, and shape layers.
 * @summary Apply photoshop edits
 */
export const modifyDocumentAsync = (
  modifyDocumentRequest: ModifyDocumentRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/documentOperations`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: modifyDocumentRequest,
    },
    options
  );
};

/**
 * Create flat image representations of a PSD in multiple formats.
 * @summary Create renditions
 */
export const createRenditionAsync = (
  createRenditionRequest: CreateRenditionRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/renditionCreate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createRenditionRequest,
    },
    options
  );
};

/**
 * Replace Smart Object in a PSD.
 * @summary Replace smart object
 */
export const replaceSmartObjectAsync = (
  replaceSmartObjectRequest: ReplaceSmartObjectRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/smartObject`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: replaceSmartObjectRequest,
    },
    options
  );
};

/**
 * Executes Photoshop Action file against a PSD, JPEG, PNG, or TIFF.
 * @summary Run photoshop actions
 */
export const playPhotoshopActionsAsync = (
  playPhotoshopActionsRequest: PlayPhotoshopActionsRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/photoshopActions`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: playPhotoshopActionsRequest,
    },
    options
  );
};

/**
 * Execute given Photoshop Action specified in actionJSON format.
 * @summary Run actionJSON
 */
export const playPhotoshopActionsJsonAsync = (
  playPhotoshopActionsJsonRequest: PlayPhotoshopActionsJsonRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/actionJSON`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: playPhotoshopActionsJsonRequest,
    },
    options
  );
};

/**
 * Convert an ATN file to actionJSON format.
 * @summary Convert to actionJSON
 */
export const convertToActionsJsonAsync = (
  convertToActionsJsonRequest: ConvertToActionsJsonRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/actionJsonCreate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: convertToActionsJsonRequest,
    },
    options
  );
};

/**
 * This endpoint identifies the subject of the input file and automatically crops the image to keep the subject in the center. Check the status of this process with the `Get Status` endpoint.
 * @summary Product crop
 */
export const applyAutoCropAsync = (
  applyAutoCropRequest: ApplyAutoCropRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/productCrop`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: applyAutoCropRequest,
    },
    options
  );
};

/**
 * Apply depth blur to an image input.
 * @summary Depth blur
 */
export const applyDepthBlurAsync = (
  applyDepthBlurRequest: ApplyDepthBlurRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/depthBlur`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: applyDepthBlurRequest,
    },
    options
  );
};

/**
 * Change the contents of a text layer in a PSD, e.g. for localization.
 * @summary Edit text layer
 */
export const editTextLayerAsync = (
  editTextLayerRequest: EditTextLayerRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/text`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: editTextLayerRequest,
    },
    options
  );
};

/**
 * Create artboards from multiple PSD inputs.
 * @summary Create artboards
 */
export const createArtboardAsync = (
  createArtboardRequest: CreateArtboardRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobStatusLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobStatusLinkResponse>(
    {
      url: `/pie/psdService/artboardCreate`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: createArtboardRequest,
    },
    options
  );
};

/**
 * Given an image, this API generates semantic masks for the foreground objects and a list of background masks that segment the background elements of the image. Each mask includes a label and a URL for the mask image. To check the status of this process, utilize the `Get Status - V1` endpoint.
 * @summary Generate object and background masks
 */
export const maskObjects = (
  maskObjectsRequest: MaskObjectsRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobLinkResponse>(
    {
      url: `/v1/mask-objects`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: maskObjectsRequest,
    },
    options
  );
};

/**
 * This endpoint processes an input image of a human, then identifies and creates masks for various items and sections on the body including sunglasses, hats, upper body apparel, lower body apparel, left arm, right arm, and more. The API returns an array of masks corresponding to each detected item and body part. To check the status of this process, utilize the `Get Status - V1` endpoint.
 * @summary Generate human item masks
 */
export const maskBodyParts = (
  maskBodyPartsRequest: MaskBodyPartsRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobLinkResponse>(
    {
      url: `/v1/mask-body-parts`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: maskBodyPartsRequest,
    },
    options
  );
};

/**
 * This endpoint allows you to refine and improve the quality of existing masks. The API will enhance the edges and details of the provided masks to create more precise and accurate results. To check the status of this process, utilize the `Get Status - V1` endpoint.
 * @summary Refine mask
 */
export const refineMask = (
  refineMaskRequest: RefineMaskRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobLinkResponse>(
    {
      url: `/v1/refine-mask`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: refineMaskRequest,
    },
    options
  );
};

/**
 * This endpoint allows you to inpaint masked areas within an image. The API will remove the objects represented by these masks and inpaint the specified areas. Provide a list of masks corresponding to the regions you wish to modify. To check the status of this process, utilize the `Get Status - V1` endpoint.
 * @summary Fill masked areas
 */
export const fillMaskedAreas = (
  fillMaskedAreasRequest: FillMaskedAreasRequest,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<JobLinkResponse>>
) => {
  return customPhotoshopAxiosInstance<JobLinkResponse>(
    {
      url: `/v1/fill-masked-areas`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: fillMaskedAreasRequest,
    },
    options
  );
};

/**
 * This endpoint allows you to check the status of an asynchronous job for a masking operation. The schema of a 200 response varies depending on the status of the job. A job with a `succeeded` status will include the results in the response and the result objects vary depending on the operation.
 * @summary Get status - v1
 */
export const getJobStatus = (
  { jobId }: GetJobStatusPathParameters,
  options?: SecondParameter<typeof customPhotoshopAxiosInstance<GetJobStatus200>>
) => {
  return customPhotoshopAxiosInstance<GetJobStatus200>(
    { url: `/v1/status/${jobId}`, method: 'GET' },
    options
  );
};

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

export type SenseiJobStatusResult = NonNullable<Awaited<ReturnType<typeof senseiJobStatus>>>;
export type RemoveBackgroundAsyncResult = NonNullable<
  Awaited<ReturnType<typeof removeBackgroundAsync>>
>;
export type RemoveBackgroundResult = NonNullable<Awaited<ReturnType<typeof removeBackground>>>;
export type FacadeJobStatusResult = NonNullable<Awaited<ReturnType<typeof facadeJobStatus>>>;
export type CreateMaskAsyncResult = NonNullable<Awaited<ReturnType<typeof createMaskAsync>>>;
export type PsJobStatusResult = NonNullable<Awaited<ReturnType<typeof psJobStatus>>>;
export type GetDocumentManifestAsyncResult = NonNullable<
  Awaited<ReturnType<typeof getDocumentManifestAsync>>
>;
export type CreateDocumentAsyncResult = NonNullable<
  Awaited<ReturnType<typeof createDocumentAsync>>
>;
export type ModifyDocumentAsyncResult = NonNullable<
  Awaited<ReturnType<typeof modifyDocumentAsync>>
>;
export type CreateRenditionAsyncResult = NonNullable<
  Awaited<ReturnType<typeof createRenditionAsync>>
>;
export type ReplaceSmartObjectAsyncResult = NonNullable<
  Awaited<ReturnType<typeof replaceSmartObjectAsync>>
>;
export type PlayPhotoshopActionsAsyncResult = NonNullable<
  Awaited<ReturnType<typeof playPhotoshopActionsAsync>>
>;
export type PlayPhotoshopActionsJsonAsyncResult = NonNullable<
  Awaited<ReturnType<typeof playPhotoshopActionsJsonAsync>>
>;
export type ConvertToActionsJsonAsyncResult = NonNullable<
  Awaited<ReturnType<typeof convertToActionsJsonAsync>>
>;
export type ApplyAutoCropAsyncResult = NonNullable<Awaited<ReturnType<typeof applyAutoCropAsync>>>;
export type ApplyDepthBlurAsyncResult = NonNullable<
  Awaited<ReturnType<typeof applyDepthBlurAsync>>
>;
export type EditTextLayerAsyncResult = NonNullable<Awaited<ReturnType<typeof editTextLayerAsync>>>;
export type CreateArtboardAsyncResult = NonNullable<
  Awaited<ReturnType<typeof createArtboardAsync>>
>;
export type MaskObjectsResult = NonNullable<Awaited<ReturnType<typeof maskObjects>>>;
export type MaskBodyPartsResult = NonNullable<Awaited<ReturnType<typeof maskBodyParts>>>;
export type RefineMaskResult = NonNullable<Awaited<ReturnType<typeof refineMask>>>;
export type FillMaskedAreasResult = NonNullable<Awaited<ReturnType<typeof fillMaskedAreas>>>;
export type GetJobStatusResult = NonNullable<Awaited<ReturnType<typeof getJobStatus>>>;
